import { useContext, useEffect, useState } from "react";
import { FirstInputFormDataContext } from "../../../context/FirstInputFormDataContext";
import { SecondInputFormDataContext } from "../../../context/SecondInputFormDataContext";
import formatNumber from "../../../utils/formatNumber";
import Style from "./NumberInputSelect.module.css";

const currencySign = "EUR";

const NumberInputSelect = ({
  name,
  label,
  initialValue,
  setSpecificState,
  isFirstInputForm,
  isSecondInputForm,
  isInFirstInput = false,
  isError,
  maxNumber = 9999999999,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [percentageSelected, setPercentageSelected] = useState(false);
  const [currencySelected, setCurrencySelected] = useState(true);
  const [finalValue, setFinalValue] = useState(0);

  const { totalValue } = useContext(
    isFirstInputForm
      ? FirstInputFormDataContext
      : isSecondInputForm
      ? SecondInputFormDataContext
      : {}
  );

  useEffect(() => {
    const savedSelection = sessionStorage.getItem(`${name}_selection`);
    if (savedSelection) {
      if (savedSelection === "currency") {
        setCurrencySelected(true);
        setPercentageSelected(false);
      } else if (savedSelection === "percentage") {
        setCurrencySelected(false);
        setPercentageSelected(true);
      }
    }
  }, [name]);

  useEffect(() => {
    const savedValue = sessionStorage.getItem(name);
    if (savedValue) {
      const parsedValue = parseFloat(savedValue);
      if (currencySelected) {
        setInputValue(formatNumberAndCheckIsValid(parsedValue));
        setFinalValue(parsedValue);
        setSpecificState(parsedValue);
      } else if (percentageSelected) {
        const calculatedValue = totalValue * (parsedValue / 100);
        setFinalValue(calculatedValue);
        setSpecificState(calculatedValue);
        setInputValue(formatNumberAndCheckIsValid(parsedValue));
      }
    }
  }, [
    currencySelected,
    percentageSelected,
    totalValue,
    name,
    setSpecificState,
  ]);

  useEffect(() => {
    const savedValue = sessionStorage.getItem(name);
    if (savedValue) {
      const parsedValue = parseFloat(savedValue);
      setInputValue(formatNumberAndCheckIsValid(parsedValue));
      setSpecificState(parsedValue);
    } else if (initialValue) {
      const parsedValue = parseFloat(initialValue);
      setInputValue(formatNumberAndCheckIsValid(parsedValue));
      setSpecificState(parsedValue);
      setFinalValue(parsedValue);
    }
  }, [initialValue, name, setSpecificState]);

  useEffect(() => {
    if (!inputValue || inputValue === "") {
      return;
    }
    if (currencySelected) {
      setFinalValue(parseFloat(inputValue.replace(/,/g, "")));
      setSpecificState(parseFloat(inputValue.replace(/,/g, "")));
    } else if (percentageSelected) {
      const calculatedValue =
        totalValue * (parseFloat(inputValue.replace(/,/g, "")) / 100);
      setFinalValue(calculatedValue);
      setSpecificState(calculatedValue);
    }
  }, [
    currencySelected,
    percentageSelected,
    inputValue,
    totalValue,
    setSpecificState,
  ]);

  const handleChange = (event) => {
    let newValue = event.target.value;

    if (Number(newValue.replace(/,/g, "")) > maxNumber) {
      return;
    }

    // Handle empty input
    if (newValue === "") {
      setInputValue("");
      setFinalValue(null);
      setSpecificState(null);
      sessionStorage.removeItem(name);
      return;
    }

    newValue = formatNumber(newValue);

    // limit percentage to be 100
    if (percentageSelected && parseFloat(newValue.replace(/,/g, "")) > 100) {
      newValue = "100";
    }

    setInputValue(newValue);

    if (currencySelected) {
      setFinalValue(parseFloat(newValue.replace(/,/g, "")));
      setSpecificState(parseFloat(newValue.replace(/,/g, "")));
    } else if (percentageSelected) {
      const calculatedValue =
        totalValue * (parseFloat(newValue.replace(/,/g, "")) / 100);
      setFinalValue(calculatedValue);
      setSpecificState(calculatedValue);
    }
    sessionStorage.setItem(name, newValue.replace(/,/g, ""));
  };

  const manageCurrencySelected = () => {
    setCurrencySelected(true);
    setPercentageSelected(false);

    sessionStorage.setItem(`${name}_selection`, "currency");
  };

  const managePercentageSelected = () => {
    setCurrencySelected(false);
    setPercentageSelected(true);

    // check if current input value is > 100 and limit it to 100
    let newValue = inputValue;
    if (parseFloat(newValue.replace(/,/g, "")) > 100) {
      newValue = "100";
      setInputValue(newValue);
      setSpecificState(totalValue);
      sessionStorage.setItem(name, newValue.replace(/,/g, ""));
    }
    sessionStorage.setItem(`${name}_selection`, "percentage");
  };

  // format number from 1000 to 1,000.00, etc.
  const formatNumberAndCheckIsValid = (value) => {
    if (isNaN(value) || value === null || value === undefined) {
      return "";
    }
    return formatNumber(value);
  };

  return (
    <div className={Style.NumberInputContainer}>
      <label className={Style.NumberInputLabel}>{label}</label>
      {isError && <p className={Style.ErrorText}>Ovo polje je obavezno</p>}
      <div className={Style.NumberInputHolder}>
        <div
          className={`${Style.NumberInput} ${isError ? Style.Error : ""} ${
            isInFirstInput ? Style.FirstInput : ""
          }`}
        >
          <input
            type="text"
            value={inputValue}
            placeholder="prazno"
            onChange={handleChange}
            className={Style.NumberInputInput}
          ></input>
          <button
            onClick={managePercentageSelected}
            className={
              percentageSelected ? Style.PercentageSelected : Style.Percentage
            }
          >
            %
          </button>
          <button
            onClick={manageCurrencySelected}
            className={
              currencySelected ? Style.CurrencySignSelected : Style.CurrencySign
            }
          >
            {currencySign}
          </button>
        </div>
        <div
          style={{ marginLeft: "20px", fontSize: "1.2em", alignSelf: "center" }}
        >
          {inputValue && (
            <>
              {finalValue ? formatNumber(finalValue) + " EUR - " : ""}{" "}
              {isNaN(finalValue / totalValue)
                ? ""
                : ((finalValue / totalValue) * 100).toFixed(2) +
                  "%  ukupnog iznosa"}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumberInputSelect;
